import React from "react";
import * as axios from "axios";
import {
  Checkbox,
  Button, TextField, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import {formatDate} from "./shiftUtils";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

const MONTH_LABELS = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie',
];

// Salarii only exists from August 2026 onward — the month the base
// salary/bonus history this screen depends on actually started being
// tracked, so there's nothing meaningful to show before it.
const FIRST_YEAR = 2026;
const FIRST_MONTH = 8;

const emptyExtraForm = {delivererId: '', amount: '', reason: ''};

// The current month, clamped forward to FIRST_YEAR/FIRST_MONTH — opening
// Salarii before August 2026 existed would otherwise land on a month with
// nothing to show and no way to tell why.
function currentOrFirstMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (year < FIRST_YEAR || (year === FIRST_YEAR && month < FIRST_MONTH)) {
    return {year: FIRST_YEAR, month: FIRST_MONTH};
  }
  return {year, month};
}

function formatAmount(value) {
  return `${Number(value || 0).toLocaleString('ro-RO', {minimumFractionDigits: 0, maximumFractionDigits: 2})} Lei`;
}

// Super-admin only (see Navbar.js and models.ListDelivererSalaryForMonth on
// the backend) — one row per deliverer for the selected month: base salary
// (whatever was in effect that month), bonus (every bonus period completed
// that month), Plată extra (delivered orders × Sumă for a "Plătit pe
// comandă" deliverer — see DelivererModal — otherwise 200 lei per
// "extra"/"extra_paid" scheduler day that month), Cash (same live value as
// Personal's Cash column — money the deliverer is already holding,
// collected directly from customers), Altele (the sum of every manual
// "other" entry logged for that deliverer/month — see the "Adaugă" modal
// and its history popup), and Total = base + bonus + extra pay + altele -
// cash. The footer row sums Total across every deliverer. For a past month,
// a since-deleted deliverer still shows up (dimmed, same
// deliverers-row-deleted styling as Personal/Mașini) — they still worked
// that month; the current month only shows who's actually still active,
// same rule Program.js applies to past schedule days.
export default class Salarii extends React.Component {
  state = {
    ...currentOrFirstMonth(),
    rows: [],
    loading: true,
    extraModalOpen: false,
    extraForm: emptyExtraForm,
    extraSaving: false,
    extraError: '',
    alteleHistoryAnchor: null,
    alteleHistoryDeliverer: null,
    alteleHistoryEntries: [],
    alteleHistoryLoading: false,
    copiedId: null,
  };

  componentDidMount() {
    this.getSalary();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.year !== this.state.year || prevState.month !== this.state.month) {
      this.getSalary();
    }
  }

  // "Achitat": ticked once the deliverer was paid (Total > 0) or brought the
  // money back (Total < 0). Shown at once, undone if the save fails.
  toggleSettled = async (row) => {
    const settled = !row.settled;
    const {year, month} = this.state;
    const update = (value) => this.setState(({rows}) => ({
      rows: rows.map((r) => (r.deliverer_id === row.deliverer_id ? {...r, settled: value} : r)),
    }));
    update(settled);
    try {
      const token = localStorage.getItem('access_token');
      await axios.default.put(`${baseUrl}v1/admin/deliverer/${row.deliverer_id}/salary-settled`, {year, month, settled}, {
        headers: {Authorization: `Bearer ${token}`},
      });
    } catch (e) {
      update(!settled);
      window.alert('Nu s-a putut salva „Achitat”. Încearcă din nou.');
    }
  };

  getSalary = async () => {
    this.setState({loading: true});
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/deliverer-salary`, {
        params: {year: this.state.year, month: this.state.month},
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({rows: response.data || [], loading: false});
    } catch (e) {
      this.setState({rows: [], loading: false});
    }
  };

  isAtFirstMonth = () => this.state.year === FIRST_YEAR && this.state.month === FIRST_MONTH;

  goToPreviousMonth = () => {
    if (this.isAtFirstMonth()) {
      return;
    }
    this.setState(({year, month}) => month === 1 ? {year: year - 1, month: 12} : {month: month - 1});
  };

  goToNextMonth = () => {
    this.setState(({year, month}) => month === 12 ? {year: year + 1, month: 1} : {month: month + 1});
  };

  openExtraModal = () => this.setState({extraModalOpen: true, extraForm: emptyExtraForm, extraError: ''});

  closeExtraModal = () => this.setState({extraModalOpen: false});

  saveExtra = async () => {
    const {extraForm, year, month} = this.state;
    if (!extraForm.delivererId || extraForm.amount === '' || isNaN(Number(extraForm.amount))) {
      this.setState({extraError: 'Livrator și sumă sunt obligatorii.'});
      return;
    }
    this.setState({extraSaving: true, extraError: ''});
    try {
      const token = localStorage.getItem('access_token');
      await axios.default.post(`${baseUrl}v1/admin/deliverer/${extraForm.delivererId}/salary-extra`, {
        year, month, amount: Number(extraForm.amount), reason: extraForm.reason,
      }, {
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({extraModalOpen: false});
      await this.getSalary();
    } catch (e) {
      this.setState({extraError: 'Eroare la salvare.'});
    } finally {
      this.setState({extraSaving: false});
    }
  };

  openAlteleHistory = async (event, row) => {
    this.setState({alteleHistoryAnchor: event.currentTarget, alteleHistoryDeliverer: row, alteleHistoryEntries: [], alteleHistoryLoading: true});
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/deliverer/${row.deliverer_id}/salary-extra-history`, {
        params: {year: this.state.year, month: this.state.month},
        headers: {Authorization: `Bearer ${token}`},
      });
      this.setState({alteleHistoryEntries: response.data || [], alteleHistoryLoading: false});
    } catch (e) {
      this.setState({alteleHistoryLoading: false});
    }
  };

  closeAlteleHistory = () => this.setState({alteleHistoryAnchor: null, alteleHistoryDeliverer: null});

  // Builds a plain-text payroll summary for one row and copies it to the
  // clipboard — fetches that deliverer's Altele entries for the selected
  // month fresh rather than relying on the history popup's state, since
  // that's only populated once an admin has actually opened it.
  copyRow = async (row) => {
    const {year, month} = this.state;
    let alteleEntries = [];
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.default.get(`${baseUrl}v1/admin/deliverer/${row.deliverer_id}/salary-extra-history`, {
        params: {year, month},
        headers: {Authorization: `Bearer ${token}`},
      });
      alteleEntries = response.data || [];
    } catch (e) {
      // Copy the rest of the summary anyway — an incomplete Altele
      // breakdown beats no summary at all.
    }

    const lines = [
      `${row.name}:`,
      `Salariu: ${formatAmount(row.base_salary)}`,
      `Bonus: ${formatAmount(row.bonus)}`,
      `Zile extra: ${formatAmount(row.extra_pay)}`,
      `Cash: -${formatAmount(row.cash)}`,
      ...alteleEntries.map((entry) => `${entry.reason || 'Altele'}: ${formatAmount(entry.amount)}`),
      `Total: ${formatAmount(row.total)}`,
    ];

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      this.setState({copiedId: row.deliverer_id});
      setTimeout(() => this.setState((state) => state.copiedId === row.deliverer_id ? {copiedId: null} : null), 1500);
    } catch (e) {
      alert('Nu s-a putut copia în clipboard.');
    }
  };

  render() {
    const {
      year, month, rows, loading,
      extraModalOpen, extraForm, extraSaving, extraError,
      alteleHistoryAnchor, alteleHistoryDeliverer, alteleHistoryEntries, alteleHistoryLoading,
    } = this.state;
    // A deliverer with nothing at all for the month (no salary, bonus, cash
    // held, extra pay, or Altele) is just noise on a payroll screen — the
    // "Adaugă" dropdown below still lists everyone, though, since that's
    // exactly how someone would get their first Altele entry logged.
    const visibleRows = rows.filter((row) => (
      row.base_salary || row.bonus || row.extra_pay || row.cash || row.altele || row.total
    ));
    const sumColumn = (field) => visibleRows.reduce((sum, row) => sum + (row[field] || 0), 0);
    const totals = {
      base_salary: sumColumn('base_salary'),
      bonus: sumColumn('bonus'),
      extra_pay: sumColumn('extra_pay'),
      cash: sumColumn('cash'),
      altele: sumColumn('altele'),
      total: sumColumn('total'),
    };

    return (
      <div className="deliverers-page deliverers-page-scroll">
        <div className="deliverers-section">
          <div className="deliverers-section-header">
            <h3 className="deliverers-section-title">Salarii</h3>
            <div className="deliverers-header-actions">
              <Button variant="contained" color="primary" onClick={this.openExtraModal}>
                + Adaugă
              </Button>
            </div>
          </div>

          <div className="deliverers-scheduler-nav">
            <Button onClick={this.goToPreviousMonth} disabled={this.isAtFirstMonth()}>
              &lt; Luna anterioară
            </Button>
            <span className="deliverers-scheduler-week-label">{MONTH_LABELS[month - 1]} {year}</span>
            <Button onClick={this.goToNextMonth}>Luna următoare &gt;</Button>
          </div>

          {loading ? (
            <div className="loader-container">
              <div className="loader"/>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="deliverers-empty-state">Niciun livrator găsit.</div>
          ) : (
            <div className="deliverers-table-scroll">
              <table className="deliverers-table">
                <thead>
                <tr>
                  <th>Livrator</th>
                  <th className="deliverers-table-center">Salariu de bază</th>
                  <th className="deliverers-table-center">Bonus</th>
                  <th className="deliverers-table-center">Plată extra</th>
                  <th className="deliverers-table-center">Cash</th>
                  <th className="deliverers-table-center">Altele</th>
                  <th className="deliverers-table-center">Total</th>
                  <th className="deliverers-table-center">Achitat</th>
                  <th>Acțiuni</th>
                </tr>
                </thead>
                <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.deliverer_id} className={row.deleted ? 'deliverers-row-deleted' : ''}>
                    <td>{row.name}</td>
                    <td className="deliverers-table-center">{formatAmount(row.base_salary)}</td>
                    <td className="deliverers-table-center">{formatAmount(row.bonus)}</td>
                    <td className="deliverers-table-center">{formatAmount(row.extra_pay)}</td>
                    <td className="deliverers-table-center">{formatAmount(row.cash)}</td>
                    <td className="deliverers-table-center">
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        {formatAmount(row.altele)}
                        <IconButton size="small" title="Istoric Altele" onClick={(event) => this.openAlteleHistory(event, row)}>
                          <HistoryIcon fontSize="small"/>
                        </IconButton>
                      </div>
                    </td>
                    <td className={`deliverers-table-center ${row.total >= 0 ? 'deliverers-salary-total-positive' : 'deliverers-salary-total-negative'}`}>
                      <strong>{formatAmount(row.total)}</strong>
                    </td>
                    <td className="deliverers-table-center">
                      <Checkbox
                        color="primary"
                        checked={!!row.settled}
                        onChange={() => this.toggleSettled(row)}
                        title={row.total >= 0 ? 'Plătit livratorului' : 'Bani aduși de livrator'}
                        inputProps={{'aria-label': `Achitat ${row.name}`}}
                      />
                    </td>
                    <td>
                      <IconButton size="small" title="Copiază rezumat" onClick={() => this.copyRow(row)}>
                        {this.state.copiedId === row.deliverer_id ? <CheckIcon fontSize="small"/> : <FileCopyIcon fontSize="small"/>}
                      </IconButton>
                    </td>
                  </tr>
                ))}
                <tr className="deliverers-table-total-row">
                  <td>Total general</td>
                  <td className="deliverers-table-center">{formatAmount(totals.base_salary)}</td>
                  <td className="deliverers-table-center">{formatAmount(totals.bonus)}</td>
                  <td className="deliverers-table-center">{formatAmount(totals.extra_pay)}</td>
                  <td className="deliverers-table-center">{formatAmount(totals.cash)}</td>
                  <td className="deliverers-table-center">{formatAmount(totals.altele)}</td>
                  <td className={`deliverers-table-center ${totals.total >= 0 ? 'deliverers-salary-total-positive' : 'deliverers-salary-total-negative'}`}>
                    {formatAmount(totals.total)}
                  </td>
                  <td className="deliverers-table-center">
                    {visibleRows.filter((row) => row.settled).length}/{visibleRows.length}
                  </td>
                  <td/>
                </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Dialog open={extraModalOpen} onClose={this.closeExtraModal} fullWidth maxWidth="xs">
          <DialogTitle>Adaugă Altele — {MONTH_LABELS[month - 1]} {year}</DialogTitle>
          <DialogContent>
            <TextField
              select
              label="Livrator"
              value={extraForm.delivererId}
              onChange={(event) => this.setState({extraForm: {...extraForm, delivererId: event.target.value}})}
              fullWidth
              margin="dense"
            >
              {rows.map((row) => (
                <MenuItem key={row.deliverer_id} value={row.deliverer_id}>{row.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Sumă"
              type="number"
              value={extraForm.amount}
              onChange={(event) => this.setState({extraForm: {...extraForm, amount: event.target.value}})}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Motiv"
              value={extraForm.reason}
              onChange={(event) => this.setState({extraForm: {...extraForm, reason: event.target.value}})}
              fullWidth
              multiline
              margin="dense"
            />
            {extraError && <div className="deliverer-modal-error">{extraError}</div>}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeExtraModal} disabled={extraSaving}>Anulează</Button>
            <Button onClick={this.saveExtra} disabled={extraSaving} variant="contained" color="primary">
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Menu anchorEl={alteleHistoryAnchor} open={!!alteleHistoryAnchor} onClose={this.closeAlteleHistory}>
          {alteleHistoryDeliverer && (
            <div className="deliverers-status-menu-header">
              Istoric Altele — {alteleHistoryDeliverer.name}
            </div>
          )}
          {alteleHistoryLoading ? (
            <MenuItem disabled>Se încarcă...</MenuItem>
          ) : alteleHistoryEntries.length === 0 ? (
            <MenuItem disabled>Nicio intrare înregistrată</MenuItem>
          ) : alteleHistoryEntries.map((entry) => (
            <MenuItem key={entry.id} disabled>
              {formatDate(new Date(entry.updated))} — {formatAmount(entry.amount)}
              {entry.reason && ` (${entry.reason})`}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  }
}
